/**
 * Real backend. Reads/writes go straight to Supabase (RLS keeps each user to
 * their own rows); AI and heavy math go to the Worker.
 */
import type { PostgrestError, RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { parseGtin } from '@engine/barcode';
import { log } from '../lib/logger';
import { todayIn } from '../lib/dates';
import {
  clearLocalSettings, manualDay, normalizeSettings, readLocalActivity, readLocalSettings, settingsRow, toActivityDay, writeLocalActivity, writeLocalSettings,
} from '../lib/settings';
import {
  DEFAULT_SETTINGS,
  type ActivityDay, type AnalyzeMealRequest, type AnalyzeMealResponse, type BarcodeLookupResponse, type BodyGoal, type BodyGoalInput, type BodyStat, type BodyStatInsert,
  type CompleteNutrientsRequest, type CompleteNutrientsResponse, type DailyMetric, type DietSettings, type Food, type FoodAiFill, type FoodInsert, type FoodSearchHit,
  type FoodSubmissionInsert, type FoodWithFill, type ManualActivityInput, type MealInsert, type MealLog, type NutrientMap, type NutrientSourceMap, type Profile, type ProfileUpdate,
  type ReadLabelRequest, type ReadLabelResponse,
  type AdminSubmission, type FoodUpdate, type MySubmission, type ReviewedFood, type SubmissionOriginal, type SubmissionStatus, type SubmissionTarget,
} from '../lib/types';
import type { DataSource, SettingsResult, TrajectoryRequest, TrajectoryResponse } from './source';
import { getSupabase } from './supabaseClient';
import { callWorker } from './workerApi';

const PROFILE_COLS: string = 'id,display_name,sex,birth_date,height_cm,unit_system,timezone,activity_level,target_calories,target_protein_g,target_carbs_g,target_fat_g';
/** Added by 20260922210000_diet_cycle_profile.sql. Until that migration runs, profiles are read and saved without them. */
const CYCLE_COLS: string = 'cycle_last_period_start,cycle_length_days';
/** Lifting-owned (20260921140000_lifting_profile.sql): read only, as a fallback body-fat %. */
const LIFTING_COLS: string = 'body_fat_percent';
const MEAL_COLS: string = 'id,eaten_at,local_date,meal_type,description,source,calories,protein_g,carbs_g,fat_g,fiber_g,sugar_g,alcohol_g,sodium_mg,potassium_mg,items,ai_model,ai_confidence';
/** Added by 20260919000000_food_database.sql. Until that migration runs, meals are read and saved without it. */
const MEAL_NUTRIENTS_COL: string = 'nutrients';
/** Added by 20260925220000_diet_energy_diary_submissions.sql, like the two tables below. */
const MEAL_SECTION_COL: string = 'diary_section';
const SETTINGS_COLS: string = 'energy_mode,bmr_mode,bmr_custom_kcal,include_tef,macro_plan,diary_layout,goal_pace_pct,tef_profile';
const ACTIVITY_COLS: string = 'local_date,source,active_kcal,total_kcal,steps,steps_uncovered,distance_m,exercise_minutes,avg_hr,min_hr,max_hr,resting_hr,hrv_rmssd_ms,spo2_pct,respiratory_rate,vo2max,sleep_minutes,exercises,origins,day_complete,notes,synced_at';
/** Live on their own channel, so a database without them can't break the main one. */
const LIVE_TABLES_2: ReadonlyArray<{ table: string; owner: string }> = [
  { table: 'diet_settings', owner: 'user_id' }, { table: 'diet_activity_days', owner: 'user_id' },
];
const FOOD_COLS: string = 'id,owner_id,source,fdc_id,name,brand,category,nutrients,nutrient_sources,portions,notes,ai_model,updated_at';
/** Added by 20260924000000_diet_food_barcodes.sql. Until then, scanned foods are saved as plain 'user' foods. */
const FOOD_BARCODE_COL: string = 'barcode';
/** Tables in the supabase_realtime publication (20260924010000_diet_realtime.sql). */
const LIVE_TABLES: ReadonlyArray<{ table: string; owner: string }> = [
  { table: 'meal_logs', owner: 'user_id' }, { table: 'body_stats', owner: 'user_id' }, { table: 'body_goals', owner: 'user_id' },
  { table: 'foods', owner: 'owner_id' }, { table: 'food_ai_fills', owner: 'user_id' },
];
const BODY_COLS: string = 'id,measured_at,local_date,weight_kg,body_fat_pct,lean_mass_kg,source,is_fasted,notes';
const GOAL_COLS: string = 'id,target_weight_kg,target_lean_mass_kg,target_body_fat_pct,target_date,planned_daily_calories,is_active,notes';
const METRIC_COLS: string = 'day,meal_count,calories,protein_g,carbs_g,fat_g,fiber_g,alcohol_g,sodium_mg,potassium_mg,weigh_in_count,weight_kg,body_fat_pct,lean_mass_kg';

/** PostgREST returns numeric columns as numbers, but be defensive. */
const n = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0));
const nn = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));

/** jsonb {key: number} → NutrientMap, dropping anything non-numeric. */
function numMap(v: unknown): NutrientMap {
  const out: NutrientMap = {};
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      const n: number = typeof x === 'number' ? x : Number(x);
      if (Number.isFinite(n)) out[k] = n;
    }
  }
  return out;
}

function toFood(r: Record<string, unknown>): Food {
  return {
    id: n(r.id),
    owner_id: (r.owner_id as string | null) ?? null,
    source: r.source as Food['source'],
    fdc_id: nn(r.fdc_id),
    name: String(r.name),
    brand: (r.brand as string | null) ?? null,
    category: (r.category as string | null) ?? null,
    nutrients: numMap(r.nutrients),
    nutrient_sources: (r.nutrient_sources && typeof r.nutrient_sources === 'object' ? r.nutrient_sources : {}) as NutrientSourceMap,
    portions: Array.isArray(r.portions) ? (r.portions as Food['portions']) : [],
    notes: (r.notes as string | null) ?? null,
    ai_model: (r.ai_model as string | null) ?? null,
    updated_at: String(r.updated_at ?? ''),
    barcode: (r.barcode as string | null | undefined) ?? null,
  };
}

function fail(op: string, error: PostgrestError): never {
  log.error('supabase.error', { op, code: error.code, message: error.message, details: error.details, hint: error.hint });
  throw new Error(`${error.message}${error.hint ? ` (${error.hint})` : ''}`);
}

/** 42703: unknown column in a select. PGRST204: unknown column in a write body. */
const missingColumn = (error: PostgrestError): boolean => error.code === '42703' || error.code === 'PGRST204';
const missingMealNutrients = (error: PostgrestError): boolean => missingColumn(error) && error.message.includes(MEAL_NUTRIENTS_COL);
const missingMealSection = (error: PostgrestError): boolean => missingColumn(error) && error.message.includes(MEAL_SECTION_COL);
const missingFoodBarcode = (error: PostgrestError): boolean => missingColumn(error) && error.message.includes(FOOD_BARCODE_COL);
/** A 'barcode' food refused because the barcode migration has not run: unknown column, CHECK (23514) or the insert policy (42501). */
const barcodeSourceRefused = (error: PostgrestError): boolean => missingFoodBarcode(error) || error.code === '23514' || error.code === '42501';

/** PGRST202/PGRST205: function/table not in the schema cache. 42883/42P01: the same, from Postgres. */
const FOOD_DB_MISSING_CODES: ReadonlySet<string> = new Set<string>(['PGRST202', 'PGRST205', '42883', '42P01']);
/** PGRST205/42P01: the table is not there (migration not applied yet). */
const missingTable = (error: PostgrestError): boolean => error.code === 'PGRST205' || error.code === '42P01';
export const FOOD_DB_MISSING: string = 'The food database is not set up on the server yet (migration 20260919000000_food_database.sql), so food search and saved foods are unavailable. Describe it, Photo and Manual still work.';

/** fail(), but a missing food-database object gets a plain explanation instead of the schema-cache text. */
function failFoods(op: string, error: PostgrestError): never {
  if (!FOOD_DB_MISSING_CODES.has(error.code)) fail(op, error);
  log.error('supabase.food_db_missing', { op, code: error.code, message: error.message });
  throw new Error(FOOD_DB_MISSING);
}

function withoutCycle(patch: ProfileUpdate): ProfileUpdate {
  const rest: ProfileUpdate = { ...patch };
  delete rest.cycle_last_period_start;
  delete rest.cycle_length_days;
  return rest;
}

function mealRow(meal: MealInsert, withNutrients: boolean, withSection: boolean): Partial<MealInsert> {
  const row: Partial<MealInsert> = { ...meal };
  if (!withNutrients) delete row.nutrients;
  if (!withSection || row.diary_section === undefined || row.diary_section === null) delete row.diary_section;
  return row;
}

function toProfile(r: Record<string, unknown>): Profile {
  return {
    id: String(r.id),
    display_name: (r.display_name as string | null) ?? null,
    sex: (r.sex as Profile['sex']) ?? null,
    birth_date: (r.birth_date as string | null) ?? null,
    height_cm: nn(r.height_cm),
    unit_system: (r.unit_system as Profile['unit_system']) ?? 'imperial',
    timezone: (r.timezone as string) ?? 'America/Chicago',
    activity_level: (r.activity_level as Profile['activity_level']) ?? null,
    target_calories: nn(r.target_calories),
    target_protein_g: nn(r.target_protein_g),
    target_carbs_g: nn(r.target_carbs_g),
    target_fat_g: nn(r.target_fat_g),
    cycle_last_period_start: (r.cycle_last_period_start as string | null | undefined) ?? null,
    cycle_length_days: nn(r.cycle_length_days),
    body_fat_percent: nn(r.body_fat_percent),
  };
}

function toMeal(r: Record<string, unknown>): MealLog {
  return {
    id: String(r.id),
    eaten_at: String(r.eaten_at),
    local_date: String(r.local_date),
    meal_type: r.meal_type as MealLog['meal_type'],
    diary_section: (r.diary_section as string | null | undefined) ?? null,
    description: String(r.description),
    source: r.source as MealLog['source'],
    calories: n(r.calories), protein_g: n(r.protein_g), carbs_g: n(r.carbs_g), fat_g: n(r.fat_g),
    fiber_g: nn(r.fiber_g), sugar_g: nn(r.sugar_g), alcohol_g: nn(r.alcohol_g),
    sodium_mg: nn(r.sodium_mg), potassium_mg: nn(r.potassium_mg),
    items: Array.isArray(r.items) ? (r.items as MealLog['items']) : [],
    nutrients: numMap(r.nutrients),
    ai_model: (r.ai_model as string | null) ?? null,
    ai_confidence: nn(r.ai_confidence),
  };
}

/** Storage bucket for submission photos (20260928100000_diet_food_review.sql). */
const PHOTO_BUCKET: string = 'food-photos';

function toTarget(v: unknown): SubmissionTarget | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  return {
    id: n(r.id), source: r.source as SubmissionTarget['source'], name: String(r.name ?? ''),
    brand: (r.brand as string | null) ?? null, barcode: (r.barcode as string | null) ?? null,
    nutrients: numMap(r.nutrients),
    nutrient_sources: (r.nutrient_sources && typeof r.nutrient_sources === 'object' ? r.nutrient_sources : {}) as NutrientSourceMap,
    portions: Array.isArray(r.portions) ? (r.portions as SubmissionTarget['portions']) : [],
    notes: (r.notes as string | null) ?? null,
  };
}

function toAdminSubmission(r: Record<string, unknown>): AdminSubmission {
  const original: SubmissionOriginal = r.original && typeof r.original === 'object' ? { ...(r.original as SubmissionOriginal) } : {};
  if (original.nutrients) original.nutrients = numMap(original.nutrients);
  return {
    id: n(r.id), kind: r.kind === 'correction' ? 'correction' : 'new', status: r.status as SubmissionStatus,
    created_at: String(r.created_at), reviewed_at: (r.reviewed_at as string | null) ?? null,
    submitter_id: String(r.submitter_id), submitter_email: (r.submitter_email as string | null) ?? null,
    barcode: (r.barcode as string | null) ?? null, name: String(r.name ?? ''), brand: (r.brand as string | null) ?? null,
    serving_label: (r.serving_label as string | null) ?? null, serving_grams: nn(r.serving_grams), servings_per_container: nn(r.servings_per_container),
    label: r.label && typeof r.label === 'object' ? (r.label as Record<string, unknown>) : {},
    nutrients: numMap(r.nutrients), ingredients: (r.ingredients as string | null) ?? null,
    allergens: Array.isArray(r.allergens) ? (r.allergens as unknown[]).map(String) : [],
    ai_model: (r.ai_model as string | null) ?? null, label_check: (r.label_check as string | null) ?? null,
    user_note: (r.user_note as string | null) ?? null, reviewer_note: (r.reviewer_note as string | null) ?? null,
    photo_paths: Array.isArray(r.photo_paths) ? (r.photo_paths as unknown[]).map(String) : [],
    original, food_id: nn(r.food_id), target_food_id: nn(r.target_food_id), promoted_food_id: nn(r.promoted_food_id),
    target: toTarget(r.target),
  };
}

function toBody(r: Record<string, unknown>): BodyStat {
  return {
    id: n(r.id),
    measured_at: String(r.measured_at),
    local_date: String(r.local_date),
    weight_kg: n(r.weight_kg),
    body_fat_pct: nn(r.body_fat_pct),
    lean_mass_kg: nn(r.lean_mass_kg),
    source: r.source as BodyStat['source'],
    is_fasted: (r.is_fasted as boolean | null) ?? null,
    notes: (r.notes as string | null) ?? null,
  };
}

function toGoal(r: Record<string, unknown>): BodyGoal {
  return {
    id: n(r.id),
    target_weight_kg: nn(r.target_weight_kg),
    target_lean_mass_kg: nn(r.target_lean_mass_kg),
    target_body_fat_pct: nn(r.target_body_fat_pct),
    target_date: (r.target_date as string | null) ?? null,
    planned_daily_calories: nn(r.planned_daily_calories),
    is_active: Boolean(r.is_active),
    notes: (r.notes as string | null) ?? null,
  };
}

function toMetric(r: Record<string, unknown>): DailyMetric {
  return {
    day: String(r.day),
    meal_count: n(r.meal_count),
    calories: n(r.calories), protein_g: n(r.protein_g), carbs_g: n(r.carbs_g), fat_g: n(r.fat_g),
    fiber_g: n(r.fiber_g), alcohol_g: n(r.alcohol_g), sodium_mg: n(r.sodium_mg), potassium_mg: n(r.potassium_mg),
    weigh_in_count: n(r.weigh_in_count),
    weight_kg: nn(r.weight_kg), body_fat_pct: nn(r.body_fat_pct), lean_mass_kg: nn(r.lean_mass_kg),
  };
}

export class SupabaseSource implements DataSource {
  public readonly kind = 'supabase' as const;
  public readonly userEmail: string;
  private readonly sb: SupabaseClient;
  private readonly userId: string;
  private cycleColumns: boolean = true;
  private liftingColumns: boolean = true;
  private mealNutrients: boolean = true;
  private mealSections: boolean = true;
  private foodBarcodes: boolean = true;
  private settingsTable: boolean = true;
  private activityTable: boolean = true;
  private submissionsTable: boolean = true;

  constructor(userId: string, userEmail: string) {
    this.sb = getSupabase();
    this.userId = userId;
    this.userEmail = userEmail;
  }

  private async token(): Promise<string> {
    const { data, error } = await this.sb.auth.getSession();
    if (error) {
      log.error('auth.get_session_failed', { message: error.message });
      throw new Error('Your session could not be read. Please sign in again.');
    }
    const t: string | undefined = data.session?.access_token;
    if (!t) throw new Error('You are signed out. Please sign in again.');
    return t;
  }

  private profileCols(): string {
    return [PROFILE_COLS, ...(this.cycleColumns ? [CYCLE_COLS] : []), ...(this.liftingColumns ? [LIFTING_COLS] : [])].join(',');
  }

  /** Drops the optional profile columns an error names. False when it names none of them. */
  private dropProfileColumns(error: PostgrestError): boolean {
    if (!missingColumn(error)) return false;
    if (this.liftingColumns && error.message.includes(LIFTING_COLS)) {
      this.liftingColumns = false;
      log.warn('profiles.lifting_columns_missing', { code: error.code, message: error.message });
      return true;
    }
    if (this.cycleColumns && error.message.includes('cycle_')) {
      this.cycleColumns = false;
      log.warn('profiles.cycle_columns_missing', { code: error.code, message: error.message });
      return true;
    }
    return false;
  }

  private mealCols(): string {
    return [MEAL_COLS, ...(this.mealNutrients ? [MEAL_NUTRIENTS_COL] : []), ...(this.mealSections ? [MEAL_SECTION_COL] : [])].join(',');
  }

  /** Drops the optional meal columns an error names. False when it names none of them. */
  private dropMealColumns(error: PostgrestError): boolean {
    if (this.mealNutrients && missingMealNutrients(error)) { this.dropMealNutrients(error); return true; }
    if (this.mealSections && missingMealSection(error)) {
      this.mealSections = false;
      log.warn('meal_logs.diary_section_missing', { code: error.code, message: error.message });
      return true;
    }
    return false;
  }

  private foodCols(): string {
    return this.foodBarcodes ? `${FOOD_COLS},${FOOD_BARCODE_COL}` : FOOD_COLS;
  }

  private dropFoodBarcodes(error: PostgrestError): void {
    this.foodBarcodes = false;
    log.warn('foods.barcode_column_missing', { code: error.code, message: error.message });
  }

  private dropMealNutrients(error: PostgrestError): void {
    this.mealNutrients = false;
    log.warn('meal_logs.nutrients_missing', { code: error.code, message: error.message });
  }

  async getProfile(): Promise<Profile> {
    const read = () => this.sb.from('profiles').select(this.profileCols()).eq('id', this.userId).maybeSingle();
    let { data, error } = await read();
    for (let i = 0; i < 2 && error && this.dropProfileColumns(error); i++) ({ data, error } = await read());
    if (error) fail('profiles.select', error);
    if (!data) throw new Error('Your profile row is missing. It should be created automatically at sign-up; check the on_auth_user_created trigger.');
    return toProfile(data as unknown as Record<string, unknown>);
  }

  async updateProfile(patch: ProfileUpdate): Promise<Profile> {
    const write = () => this.sb.from('profiles').update(this.cycleColumns ? patch : withoutCycle(patch)).eq('id', this.userId).select(this.profileCols()).single();
    let { data, error } = await write();
    for (let i = 0; i < 2 && error && this.dropProfileColumns(error); i++) ({ data, error } = await write());
    if (error) fail('profiles.update', error);
    log.info('profile.updated', { fields: Object.keys(patch) });
    return toProfile(data as unknown as Record<string, unknown>);
  }

  async listMeals(day: string): Promise<MealLog[]> {
    const read = () => this.sb.from('meal_logs').select(this.mealCols()).eq('local_date', day).order('eaten_at', { ascending: true });
    let { data, error } = await read();
    for (let i = 0; i < 2 && error && this.dropMealColumns(error); i++) ({ data, error } = await read());
    if (error) fail('meal_logs.select', error);
    return (data as unknown as Record<string, unknown>[]).map(toMeal);
  }

  async insertMeal(meal: MealInsert): Promise<MealLog> {
    // Before the diary-section column exists, a custom-section meal is still saved (as 'other').
    const write = () => this.sb.from('meal_logs').insert(mealRow(meal, this.mealNutrients, this.mealSections)).select(this.mealCols()).single();
    let { data, error } = await write();
    for (let i = 0; i < 2 && error && this.dropMealColumns(error); i++) ({ data, error } = await write());
    if (error) fail('meal_logs.insert', error);
    log.info('meal.saved', { day: meal.local_date, kcal: meal.calories, source: meal.source });
    return toMeal(data as unknown as Record<string, unknown>);
  }

  async deleteMeal(id: string): Promise<void> {
    const { error } = await this.sb.from('meal_logs').delete().eq('id', id);
    if (error) fail('meal_logs.delete', error);
    log.info('meal.deleted', { id });
  }

  async listDailyMetrics(fromDay: string, toDay: string): Promise<DailyMetric[]> {
    const { data, error } = await this.sb.from('daily_metrics').select(METRIC_COLS).gte('day', fromDay).lte('day', toDay).order('day', { ascending: true });
    if (error) fail('daily_metrics.select', error);
    return (data as unknown as Record<string, unknown>[]).map(toMetric);
  }

  async listBodyStats(fromDay: string, toDay: string): Promise<BodyStat[]> {
    const { data, error } = await this.sb.from('body_stats').select(BODY_COLS).gte('local_date', fromDay).lte('local_date', toDay).order('measured_at', { ascending: false });
    if (error) fail('body_stats.select', error);
    return (data as unknown as Record<string, unknown>[]).map(toBody);
  }

  async insertBodyStat(stat: BodyStatInsert): Promise<BodyStat> {
    const { data, error } = await this.sb.from('body_stats').insert(stat).select(BODY_COLS).single();
    if (error) fail('body_stats.insert', error);
    log.info('body_stat.saved', { day: stat.local_date, source: stat.source });
    return toBody(data as unknown as Record<string, unknown>);
  }

  async deleteBodyStat(id: number): Promise<void> {
    const { error } = await this.sb.from('body_stats').delete().eq('id', id);
    if (error) fail('body_stats.delete', error);
    log.info('body_stat.deleted', { id });
  }

  async getActiveGoal(): Promise<BodyGoal | null> {
    const { data, error } = await this.sb.from('body_goals').select(GOAL_COLS).eq('is_active', true).maybeSingle();
    if (error) fail('body_goals.select', error);
    return data ? toGoal(data as unknown as Record<string, unknown>) : null;
  }

  async saveGoal(goal: BodyGoalInput): Promise<BodyGoal> {
    // Only one active goal per user (partial unique index), so retire the old one first.
    const { error: offErr } = await this.sb.from('body_goals').update({ is_active: false }).eq('is_active', true);
    if (offErr) fail('body_goals.deactivate', offErr);
    const { data, error } = await this.sb.from('body_goals').insert({ ...goal, is_active: true }).select(GOAL_COLS).single();
    if (error) fail('body_goals.insert', error);
    log.info('goal.saved', { targetDate: goal.target_date });
    return toGoal(data as unknown as Record<string, unknown>);
  }

  // ---- settings and wearable days ------------------------------------------------
  async getSettings(): Promise<SettingsResult> {
    if (!this.settingsTable) return { settings: readLocalSettings(this.userId) ?? DEFAULT_SETTINGS, stored: 'device' };
    const { data, error } = await this.sb.from('diet_settings').select(SETTINGS_COLS).eq('user_id', this.userId).maybeSingle();
    if (error && missingTable(error)) {
      this.settingsTable = false;
      log.warn('diet_settings.missing', { code: error.code });
      return { settings: readLocalSettings(this.userId) ?? DEFAULT_SETTINGS, stored: 'device' };
    }
    if (error) fail('diet_settings.select', error);
    if (data) return { settings: normalizeSettings(data), stored: 'server' };
    // Settings saved in this browser before the migration move to the server on first read.
    const local: DietSettings | null = readLocalSettings(this.userId);
    if (local) {
      try {
        const saved: SettingsResult = await this.writeSettings(local);
        clearLocalSettings(this.userId);
        log.info('diet_settings.moved_from_browser');
        return saved;
      } catch (err) {
        log.warn('diet_settings.move_failed', { error: String(err) });
        return { settings: local, stored: 'device' };
      }
    }
    return { settings: DEFAULT_SETTINGS, stored: 'server' };
  }

  async saveSettings(patch: Partial<DietSettings>): Promise<SettingsResult> {
    const current: DietSettings = (await this.getSettings()).settings;
    const merged: DietSettings = normalizeSettings({ ...settingsRow(current), ...patch, macro_plan: patch.macro_plan === undefined ? current.macro_plan ?? {} : patch.macro_plan ?? {} });
    const saved: SettingsResult = await this.writeSettings(merged);
    log.info('diet_settings.saved', { fields: Object.keys(patch), stored: saved.stored });
    return saved;
  }

  private async writeSettings(s: DietSettings): Promise<SettingsResult> {
    if (!this.settingsTable) { writeLocalSettings(this.userId, s); return { settings: s, stored: 'device' }; }
    const { data, error } = await this.sb.from('diet_settings')
      .upsert({ user_id: this.userId, ...settingsRow(s) }, { onConflict: 'user_id' })
      .select(SETTINGS_COLS).single();
    if (error && missingTable(error)) {
      this.settingsTable = false;
      writeLocalSettings(this.userId, s);
      return { settings: s, stored: 'device' };
    }
    if (error) fail('diet_settings.upsert', error);
    return { settings: normalizeSettings(data), stored: 'server' };
  }

  async listActivityDays(fromDay: string, toDay: string): Promise<ActivityDay[]> {
    const local = (): ActivityDay[] => readLocalActivity(this.userId).filter((d) => d.local_date >= fromDay && d.local_date <= toDay);
    if (!this.activityTable) return local();
    const { data, error } = await this.sb.from('diet_activity_days').select(ACTIVITY_COLS)
      .gte('local_date', fromDay).lte('local_date', toDay).order('local_date', { ascending: true });
    if (error && missingTable(error)) {
      this.activityTable = false;
      log.warn('diet_activity_days.missing', { code: error.code });
      return local();
    }
    if (error) fail('diet_activity_days.select', error);
    return (data as unknown as Record<string, unknown>[]).map(toActivityDay);
  }

  async saveManualActivity(input: ManualActivityInput): Promise<void> {
    const today: string = todayIn();
    const keepLocal = (): void => {
      const rows: ActivityDay[] = readLocalActivity(this.userId).filter((d) => d.local_date !== input.local_date);
      writeLocalActivity(this.userId, [...rows, manualDay(input, today)]);
    };
    if (!this.activityTable) { keepLocal(); return; }
    const row = {
      user_id: this.userId, local_date: input.local_date, source: 'manual',
      active_kcal: input.active_kcal, total_kcal: input.total_kcal, steps: input.steps, resting_hr: input.resting_hr,
      hrv_rmssd_ms: input.hrv_rmssd_ms, sleep_minutes: input.sleep_minutes, notes: input.notes,
      day_complete: input.local_date < today, synced_at: new Date().toISOString(),
    };
    const { error } = await this.sb.from('diet_activity_days').upsert(row, { onConflict: 'user_id,local_date,source' });
    if (error && missingTable(error)) { this.activityTable = false; keepLocal(); return; }
    if (error) fail('diet_activity_days.upsert', error);
    log.info('activity.manual_saved', { day: input.local_date });
  }

  async deleteManualActivity(day: string): Promise<void> {
    const dropLocal = (): void => writeLocalActivity(this.userId, readLocalActivity(this.userId).filter((d) => d.local_date !== day));
    if (!this.activityTable) { dropLocal(); return; }
    const { error } = await this.sb.from('diet_activity_days').delete().eq('local_date', day).eq('source', 'manual');
    if (error && missingTable(error)) { this.activityTable = false; dropLocal(); return; }
    if (error) fail('diet_activity_days.delete', error);
  }

  async searchFoods(query: string, opts: { limit?: number; mineOnly?: boolean } = {}): Promise<FoodSearchHit[]> {
    const { data, error } = await this.sb.rpc('search_foods', { p_query: query, p_limit: opts.limit ?? 30, p_offset: 0, p_mine_only: opts.mineOnly ?? false });
    if (error) failFoods('search_foods', error);
    return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
      id: n(r.id), source: r.source as FoodSearchHit['source'], fdc_id: nn(r.fdc_id), name: String(r.name),
      brand: (r.brand as string | null) ?? null, category: (r.category as string | null) ?? null,
      energy_kcal: nn(r.energy_kcal), protein_g: nn(r.protein_g), carbs_g: nn(r.carbs_g), fat_g: nn(r.fat_g),
      nutrient_count: n(r.nutrient_count), is_mine: Boolean(r.is_mine),
    }));
  }

  async getFood(id: number): Promise<FoodWithFill> {
    const read = () => this.sb.from('foods').select(this.foodCols()).eq('id', id).maybeSingle();
    let { data, error } = await read();
    if (error && this.foodBarcodes && missingFoodBarcode(error)) { this.dropFoodBarcodes(error); ({ data, error } = await read()); }
    if (error) failFoods('foods.select', error);
    if (!data) throw new Error('That food no longer exists.');
    const food: Food = toFood(data as unknown as Record<string, unknown>);
    let fill: FoodAiFill | null = null;
    if (food.owner_id === null) {
      const res = await this.sb.from('food_ai_fills').select('food_id,nutrients,model,confidence,notes').eq('food_id', id).maybeSingle();
      if (res.error) failFoods('food_ai_fills.select', res.error);
      if (res.data) {
        const r = res.data as unknown as Record<string, unknown>;
        fill = { food_id: n(r.food_id), nutrients: numMap(r.nutrients), model: (r.model as string | null) ?? null, confidence: nn(r.confidence), notes: Array.isArray(r.notes) ? (r.notes as string[]) : [] };
      }
    }
    return { food, fill };
  }

  async createFood(food: FoodInsert): Promise<Food> {
    const plain = (): FoodInsert => {
      const rest: FoodInsert = { ...food, source: food.source === 'barcode' ? 'user' : food.source };
      delete rest.barcode;
      return rest;
    };
    const write = (row: FoodInsert) => this.sb.from('foods').insert(row).select(this.foodCols()).single();
    let { data, error } = await write(this.foodBarcodes ? food : plain());
    if (error && this.foodBarcodes && (food.source === 'barcode' || food.barcode) && barcodeSourceRefused(error)) {
      this.dropFoodBarcodes(error);
      ({ data, error } = await write(plain()));
    }
    if (error) failFoods('foods.insert', error);
    log.info('food.created', { source: food.source, nutrients: Object.keys(food.nutrients).length });
    return toFood(data as unknown as Record<string, unknown>);
  }

  async updateFoodNutrients(id: number, nutrients: NutrientMap, sources: NutrientSourceMap, aiModel: string | null): Promise<Food> {
    const patch: Record<string, unknown> = { nutrients, nutrient_sources: sources };
    if (aiModel !== null) patch.ai_model = aiModel;
    const write = () => this.sb.from('foods').update(patch).eq('id', id).select(this.foodCols()).single();
    let { data, error } = await write();
    if (error && this.foodBarcodes && missingFoodBarcode(error)) { this.dropFoodBarcodes(error); ({ data, error } = await write()); }
    if (error) failFoods('foods.update', error);
    return toFood(data as unknown as Record<string, unknown>);
  }

  async deleteFood(id: number): Promise<void> {
    const { error } = await this.sb.from('foods').delete().eq('id', id);
    if (error) failFoods('foods.delete', error);
    log.info('food.deleted', { id });
  }

  async saveAiFill(foodId: number, values: NutrientMap, model: string | null, confidence: number | null, notes: string[]): Promise<void> {
    const { error } = await this.sb.from('food_ai_fills').upsert(
      { user_id: this.userId, food_id: foodId, nutrients: values, model, confidence, notes: notes.slice(0, 20) },
      { onConflict: 'user_id,food_id' },
    );
    if (error) failFoods('food_ai_fills.upsert', error);
    log.info('food.ai_fill_saved', { foodId, values: Object.keys(values).length });
  }

  async completeNutrients(req: CompleteNutrientsRequest): Promise<CompleteNutrientsResponse> {
    return callWorker<CompleteNutrientsResponse>('/api/complete-nutrients', req, await this.token(), 75_000);
  }

  async lookupBarcode(code: string): Promise<BarcodeLookupResponse> {
    return callWorker<BarcodeLookupResponse>(`/api/barcode/${encodeURIComponent(code)}`, undefined, await this.token(), 20_000, 'GET');
  }

  async findFoodByBarcode(gtin: string): Promise<Food | null> {
    if (!this.foodBarcodes) return null;
    const { data, error } = await this.sb.from('foods').select(this.foodCols()).eq('owner_id', this.userId).eq(FOOD_BARCODE_COL, gtin).maybeSingle();
    if (error && missingFoodBarcode(error)) { this.dropFoodBarcodes(error); return null; }
    if (error) failFoods('foods.select_barcode', error);
    if (data) return toFood(data as unknown as Record<string, unknown>);

    // Shared USDA branded foods (20260924030000_diet_branded_foods.sql), under every reading of the code
    // (a UPC-E scan also tries its UPC-A form). Before that migration there are none, so this finds nothing.
    const parsed = parseGtin(gtin);
    const codes: string[] = parsed.ok ? [...new Set([gtin, ...parsed.candidates])] : [gtin];
    const shared = await this.sb.from('foods').select(this.foodCols()).is('owner_id', null).in(FOOD_BARCODE_COL, codes)
      .order('fdc_id', { ascending: false }).limit(1);
    if (shared.error && missingFoodBarcode(shared.error)) { this.dropFoodBarcodes(shared.error); return null; }
    if (shared.error) failFoods('foods.select_shared_barcode', shared.error);
    const row = (shared.data as unknown as Record<string, unknown>[] | null)?.[0];
    return row ? toFood(row) : null;
  }

  subscribeChanges(onChange: (table: string) => void): () => void {
    const open = (name: string, tables: ReadonlyArray<{ table: string; owner: string }>): RealtimeChannel => {
      const channel: RealtimeChannel = this.sb.channel(name);
      for (const { table, owner } of tables) {
        // Inserts and updates are filtered to this user (RLS applies too); deletes cannot be filtered and carry only the id.
        channel.on('postgres_changes', { event: 'INSERT', schema: 'public', table, filter: `${owner}=eq.${this.userId}` }, () => onChange(table));
        channel.on('postgres_changes', { event: 'UPDATE', schema: 'public', table, filter: `${owner}=eq.${this.userId}` }, () => onChange(table));
        channel.on('postgres_changes', { event: 'DELETE', schema: 'public', table }, () => onChange(table));
      }
      channel.subscribe((status, err) => {
        if (status === 'SUBSCRIBED') log.info('live.subscribed', { channel: name, tables: tables.length });
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') log.warn('live.subscribe_failed', { channel: name, status, error: err?.message });
      });
      return channel;
    };
    const main: RealtimeChannel = open(`diet-live-${this.userId}`, LIVE_TABLES);
    const extra: RealtimeChannel = open(`diet-live2-${this.userId}`, LIVE_TABLES_2);
    return () => { void this.sb.removeChannel(main); void this.sb.removeChannel(extra); };
  }

  async readLabel(req: ReadLabelRequest): Promise<ReadLabelResponse> {
    return callWorker<ReadLabelResponse>('/api/read-label', req, await this.token(), 90_000);
  }

  async submitFood(sub: FoodSubmissionInsert): Promise<boolean> {
    if (!this.submissionsTable) return false;
    const { error } = await this.sb.from('food_submissions').insert(sub);
    if (error && missingTable(error)) { this.submissionsTable = false; return false; }
    if (error) {
      log.warn('food_submissions.insert_failed', { code: error.code, message: error.message });
      return false;
    }
    log.info('food.submitted', { barcode: sub.barcode, kind: sub.kind ?? 'new', photos: sub.photo_paths?.length ?? 0 });
    return true;
  }

  async updateFood(id: number, patch: FoodUpdate): Promise<Food> {
    const row: FoodUpdate = { ...patch };
    if (!this.foodBarcodes) delete row.barcode;
    const { data, error } = await this.sb.from('foods').update(row).eq('id', id).eq('owner_id', this.userId).select(this.foodCols()).maybeSingle();
    if (error) failFoods('foods.update', error);
    if (!data) throw new Error('Only your own foods can be changed.');
    log.info('food.updated', { id, fields: Object.keys(patch) });
    return toFood(data as unknown as Record<string, unknown>);
  }

  async uploadFoodPhoto(image: Blob): Promise<string> {
    const ext: string = image.type === 'image/png' ? 'png' : image.type === 'image/webp' ? 'webp' : 'jpg';
    const path: string = `${this.userId}/${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
    const { error } = await this.sb.storage.from(PHOTO_BUCKET).upload(path, image, { contentType: image.type || 'image/jpeg', upsert: false });
    if (error) {
      log.warn('food_photos.upload_failed', { message: error.message });
      throw new Error(/bucket not found/i.test(error.message)
        ? 'Photo storage is not set up yet (database update 20260928100000_diet_food_review.sql).'
        : `The photo could not be uploaded: ${error.message}`);
    }
    return path;
  }

  async listMySubmissions(): Promise<MySubmission[]> {
    if (!this.submissionsTable) return [];
    const read = (cols: string) => this.sb.from('food_submissions').select(cols).eq('user_id', this.userId).order('created_at', { ascending: false }).limit(50);
    let { data, error } = await read('id,kind,status,name,barcode,reviewer_note,created_at');
    if (error && missingColumn(error)) ({ data, error } = await read('id,status,name,barcode,reviewer_note,created_at'));
    if (error && missingTable(error)) { this.submissionsTable = false; return []; }
    if (error) fail('food_submissions.select_own', error);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map((r) => ({
      id: n(r.id), kind: r.kind === 'correction' ? 'correction' : 'new', status: r.status as SubmissionStatus, name: String(r.name ?? ''),
      barcode: (r.barcode as string | null) ?? null, reviewer_note: (r.reviewer_note as string | null) ?? null, created_at: String(r.created_at),
    }));
  }

  // ---- site owner ------------------------------------------------------------------
  async isAdmin(): Promise<boolean> {
    const { data, error } = await this.sb.from('app_admins').select('user_id').eq('user_id', this.userId).maybeSingle();
    if (error) {
      if (!missingTable(error)) log.warn('app_admins.select_failed', { code: error.code, message: error.message });
      return false;
    }
    return data !== null;
  }

  async listSubmissions(status: SubmissionStatus | 'all'): Promise<AdminSubmission[]> {
    const { data, error } = await this.sb.rpc('admin_food_submissions', { p_status: status, p_limit: 200 });
    if (error) fail('admin_food_submissions', error);
    return ((data ?? []) as Array<Record<string, unknown>>).map(toAdminSubmission);
  }

  async reviewSubmission(id: number, decision: 'approve' | 'reject', note: string | null, food: ReviewedFood | null): Promise<number | null> {
    const { data, error } = await this.sb.rpc('admin_review_food_submission', { p_id: id, p_decision: decision, p_note: note, p_food: food });
    if (error) fail('admin_review_food_submission', error);
    log.info('food_submission.reviewed', { id, decision });
    return data === null || data === undefined ? null : n(data);
  }

  async photoUrls(paths: string[]): Promise<Record<string, string>> {
    if (paths.length === 0) return {};
    const { data, error } = await this.sb.storage.from(PHOTO_BUCKET).createSignedUrls(paths, 3600);
    if (error) { log.warn('food_photos.sign_failed', { message: error.message }); return {}; }
    const out: Record<string, string> = {};
    for (const d of data ?? []) if (d.path && d.signedUrl) out[d.path] = d.signedUrl;
    return out;
  }

  async analyzeMeal(req: AnalyzeMealRequest): Promise<AnalyzeMealResponse> {
    return callWorker<AnalyzeMealResponse>('/api/analyze-meal', req, await this.token(), req.image ? 60_000 : 45_000);
  }

  async computeTrajectory(req: TrajectoryRequest): Promise<TrajectoryResponse> {
    const body: Record<string, unknown> = { lookbackDays: req.lookbackDays, horizonDays: req.horizonDays };
    if (req.planDailyCalories !== undefined && req.planDailyCalories !== null) body.planDailyCalories = req.planDailyCalories;
    if (req.tdeeOverrideKcal !== undefined && req.tdeeOverrideKcal !== null) body.options = { tdeeOverrideKcal: req.tdeeOverrideKcal };
    if (req.current && (req.current.bodyFatPct != null || req.current.leanMassKg != null)) body.current = req.current;
    return callWorker<TrajectoryResponse>('/api/compute-trajectory', body, await this.token());
  }

  async signOut(): Promise<void> {
    const { error } = await this.sb.auth.signOut();
    if (error) log.warn('auth.sign_out_failed', { message: error.message });
  }
}
